import React, { useState, useEffect } from 'react';
import { Form, Button, Alert, Card, Row, Col } from 'react-bootstrap';
import axiosInstance from '../../services/axiosInstance';
import HomePagePreferences from './HomePagePreferences';

const UserProfile = () => {
  const [userProfile, setUserProfile] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      const response = await axiosInstance.get('/user/profile/');
      setUserProfile(response.data);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      setErrorMessage('Failed to load user profile');
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    // Client-side validation
    if (passwordData.new_password !== passwordData.confirm_password) {
      setErrorMessage('New passwords do not match');
      setLoading(false);
      return;
    }

    if (passwordData.new_password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      setLoading(false);
      return;
    }

    try {
      await axiosInstance.post('/change-password/', passwordData);
      setSuccessMessage('Password changed successfully!');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setShowPasswordForm(false);
    } catch (error) {
      if (error.response?.data) {
        const errorData = error.response.data;
        if (errorData.current_password) {
          setErrorMessage(errorData.current_password[0]);
        } else if (errorData.new_password) {
          setErrorMessage(errorData.new_password[0]);
        } else if (errorData.detail) {
          setErrorMessage(errorData.detail);
        } else {
          setErrorMessage('Failed to change password. Please try again.');
        }
      } else {
        setErrorMessage('Failed to change password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!userProfile) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <h3 className="page-title">User Profile</h3>
      
      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>
          {successMessage}
        </Alert>
      )}
      
      {errorMessage && (
        <Alert variant="danger" onClose={() => setErrorMessage('')} dismissible>
          {errorMessage}
        </Alert>
      )}

      <Row>
        <Col md={6}>
          <Card className="mb-4">
            <Card.Header>
              <h5>Profile Information</h5>
            </Card.Header>
            <Card.Body>
              <div className="mb-3">
                <strong>Username:</strong> {userProfile.username}
              </div>
              <div className="mb-3">
                <strong>Email:</strong> {userProfile.email}
              </div>
              <div className="mb-3">
                <strong>First Name:</strong> {userProfile.first_name || 'Not set'}
              </div>
              <div className="mb-3">
                <strong>Last Name:</strong> {userProfile.last_name || 'Not set'}
              </div>
              <div className="mb-3">
                <strong>Member Since:</strong> {new Date(userProfile.date_joined).toLocaleDateString()}
              </div>
              <div className="mb-3">
                <strong>Last Login:</strong> {userProfile.last_login ? new Date(userProfile.last_login).toLocaleDateString() : 'Never'}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          <Card>
            <Card.Header>
              <h5>Password Management</h5>
            </Card.Header>
            <Card.Body>
              {!showPasswordForm ? (
                <div>
                  <p>Change your password to keep your account secure.</p>
                  <Button 
                    variant="primary" 
                    onClick={() => setShowPasswordForm(true)}
                  >
                    🔑 Change Password
                  </Button>
                </div>
              ) : (
                <Form onSubmit={handlePasswordSubmit}>
                  <Form.Group className="mb-3">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <Form.Label>Current Password:</Form.Label>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        style={{ 
                          padding: '0', 
                          fontSize: '0.875rem', 
                          textDecoration: 'none',
                          color: '#6c757d'
                        }}
                      >
                        {showCurrentPassword ? '🙈 Hide' : '👁️ Show'}
                      </Button>
                    </div>
                    <Form.Control
                      type={showCurrentPassword ? "text" : "password"}
                      name="current_password"
                      value={passwordData.current_password}
                      onChange={handlePasswordChange}
                      autoComplete="current-password"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      inputMode="text"
                      style={{ fontFamily: 'monospace' }}
                      required
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <Form.Label>New Password:</Form.Label>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{ 
                          padding: '0', 
                          fontSize: '0.875rem', 
                          textDecoration: 'none',
                          color: '#6c757d'
                        }}
                      >
                        {showPassword ? '🙈 Hide' : '👁️ Show'}
                      </Button>
                    </div>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      name="new_password"
                      value={passwordData.new_password}
                      onChange={handlePasswordChange}
                      autoComplete="new-password"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      inputMode="text"
                      style={{ fontFamily: 'monospace' }}
                      required
                    />
                    <Form.Text className="text-muted" style={{ fontSize: '0.75rem' }}>
                      Password must be at least 8 characters long.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <Form.Label>Confirm New Password:</Form.Label>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        style={{ 
                          padding: '0', 
                          fontSize: '0.875rem', 
                          textDecoration: 'none',
                          color: '#6c757d'
                        }}
                      >
                        {showConfirmPassword ? '🙈 Hide' : '👁️ Show'}
                      </Button>
                    </div>
                    <Form.Control
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirm_password"
                      value={passwordData.confirm_password}
                      onChange={handlePasswordChange}
                      autoComplete="new-password"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      inputMode="text"
                      style={{ fontFamily: 'monospace' }}
                      required
                    />
                  </Form.Group>

                  <div className="d-flex gap-2">
                    <Button 
                      type="submit" 
                      variant="success" 
                      disabled={loading}
                    >
                      {loading ? 'Changing Password...' : 'Update Password'}
                    </Button>
                    <Button 
                      type="button" 
                      variant="secondary"
                      onClick={() => {
                        setShowPasswordForm(false);
                        setPasswordData({
                          current_password: '',
                          new_password: '',
                          confirm_password: ''
                        });
                        setErrorMessage('');
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <HomePagePreferences />
    </div>
  );
};

export default UserProfile;

